import React from 'react';

import DownloadIcon from '@mui/icons-material/Download';
import InfoIcon from '@mui/icons-material/Info';
import { useDispatch, useSelector } from 'react-redux';

import * as selectors from '../selector';
import { nurseryIdSelector } from '../selector';
import { refreshPhotoOrderList, downloadOrderedPhotoFiles } from '../thunk';

import PurchaseDetail from './PurchaseDetail';

import type { TableBaseColumnType, TableBaseRowType } from '@/components/TableBase/Body';
import type { EntityDataPubType } from '@/domain/entityBase';
import type { ChildJson } from '@/types/domain/child/entities/childEntity';
import type { PhotoOrderJsonPub, PhotoOrderJsonPvt } from '@/types/domain/photo/entities/photoOrderEntity';

import MessageBox from '@/components/MessageBox';
import MessageBoxConfirm from '@/components/MessageBoxConfirm';
import TableBase from '@/components/TableBase';
import { PHOTO_EXPIRED_MONTH, PHOTO_EXPIRED_MONTH_NEW, PHOTO_EXPIRED_MONTH_UPDATED_DATE } from '@/domain/photo/service/entity/photoAppEntity';
import { convertDateToDisplayString, dateFormat, isOldVersion } from '@/utils/date';

/**
 * プロパティ
 */
export type Props = {
  /** 子ども */
  child: ChildJson,
}

const PurchaseHistory: React.FC<Props> = ({
  child,
}) => {
  const dispatch = useDispatch();
  const photoOrders = useSelector(selectors.photoOrdersSelector);
  const nurseryId = useSelector(nurseryIdSelector);
  const [selectedOrder, setSelectedOrder] = React.useState<
    EntityDataPubType<PhotoOrderJsonPvt, PhotoOrderJsonPub> | undefined
  >(undefined);
  const [selectedDownload, setSelectedDownload] = React.useState<
    EntityDataPubType<PhotoOrderJsonPvt, PhotoOrderJsonPub> | undefined
  >(undefined);
  React.useEffect(() => {
    dispatch(refreshPhotoOrderList({ parentChildId: `${child.parentId}_${child.childId}` }));
  }, [dispatch, child]);
  if (photoOrders.length === 0) {
    return (
      <p>購入履歴がありません。</p>
    );
  }
  const rows = photoOrders.map((v) => {
    const dt = new Date(v.data.expirationDate);
    const photoExpiredMonth = isOldVersion(
      v.data.expirationDate,
      PHOTO_EXPIRED_MONTH_UPDATED_DATE,
    ) ? PHOTO_EXPIRED_MONTH : PHOTO_EXPIRED_MONTH_NEW;
    dt.setDate(dt.getDate() + 1);
    dt.setMonth(dt.getMonth() + photoExpiredMonth);
    const isDownloadable = v.data.photos.findIndex((p) => p.photoItemId === 'download') > -1 ? '1' : '0';
    return {
      id: v.data.createDatetime,
      title: v.data.salesGroupName,
      expirationDate: dateFormat(dt, 'yyyy年MM月dd日'),
      purchaseDate: convertDateToDisplayString(v.data.createDatetime.split('T')[0]),
      totalPrice: v.data.price.toString(),
      paymentStatus: v.data.paymentStatus,
      isDownloadable,
    };
  });
  const columns: TableBaseColumnType[] = [
    { id: 'purchaseDate', label: '購入日' },
    { id: 'paymentStatus', label: '支払い' },
    { id: 'title', label: 'タイトル' },
  ];
  const actions = [
    {
      icon: <InfoIcon />,
      title: '詳細表示',
      action: async (r: TableBaseRowType) => {
        const item = photoOrders.find((v) => v.data.createDatetime === r.id);
        setSelectedOrder(item);
      },
    },
    {
      icon: <DownloadIcon />,
      title: 'まとめてZIPファイルでダウンロード',
      action: async (r: TableBaseRowType) => {
        const item = photoOrders.find((v) => v.data.createDatetime === r.id);
        setSelectedDownload(item);
      },
      disabled: (r: TableBaseRowType) => r.isDownloadable !== '1',
    },
  ];
  let comp: JSX.Element | undefined;
  if (selectedDownload) {
    const now = new Date();
    const photoExpiredMonth = isOldVersion(
      selectedDownload.data.expirationDate,
      PHOTO_EXPIRED_MONTH_UPDATED_DATE,
    ) ? PHOTO_EXPIRED_MONTH : PHOTO_EXPIRED_MONTH_NEW;
    const dt = new Date(selectedDownload.data.expirationDate);
    dt.setDate(dt.getDate() + 1);
    dt.setMonth(dt.getMonth() + photoExpiredMonth);
    if (now > dt) {
      comp = (
        <MessageBoxConfirm
          close={() => {
            setSelectedDownload(undefined);
          }}
          title="期限を過ぎました"
          content="期限を過ぎているためダウンロードできません。"
        />
      );
    } else if (selectedDownload.data.paymentStatus === '支払い済') {
      comp = (
        <MessageBox
          close={(isYes: boolean) => {
            if (isYes) {
              dispatch(downloadOrderedPhotoFiles({ nurseryId, item: selectedDownload }));
            }
            setSelectedDownload(undefined);
          }}
          title="ZIPダウンロード確認"
          content="ZIPファイルにすべての画像をまとめてダウンロードしますか？"
        />
      );
    } else {
      comp = (
        <MessageBoxConfirm
          close={() => {
            setSelectedDownload(undefined);
          }}
          title="支払い処理中です"
          content="施設により支払い処理が完了するまでダウンロードできません。"
        />
      );
    }
  }
  return (
    <div>
      <TableBase
        rows={rows}
        columns={columns}
        actions={actions}
        isPagination={false}
        isShowFilter={false}
        isShowFooter={false}
      />
      {selectedOrder && (
        <PurchaseDetail
          order={selectedOrder}
          close={() => setSelectedOrder(undefined)}
        />
      )}
      {comp}
    </div>
  );
};

export default PurchaseHistory;
